<template>
  <v-container fluid grid-list-md>
    <v-layout row wrap align-center>
      <v-flex xs12 sm6>
        <h2 class="headline">{{ isFno ? 'ISP Faults' : 'Faults' }}</h2>
        <div class="grey--text body-1">
          {{ isFno ? 'Faults logged by ISPs against your networks, and your own.' : 'Faults you have logged with the network operator.' }}
        </div>
      </v-flex>
      <v-flex xs12 sm6 class="text-sm-right">
        <v-btn flat color="primary" :loading="loading" @click="loadFaults">
          <v-icon left>refresh</v-icon>Refresh
        </v-btn>
        <v-btn color="primary" @click="openLog">
          <v-icon left>add</v-icon>{{ isFno ? 'Log own fault' : 'Log a fault' }}
        </v-btn>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex xs12 sm4>
        <v-select :items="statusFilters" v-model="statusFilter" label="Status" @change="loadFaults"></v-select>
      </v-flex>
      <v-flex xs12 sm3 v-if="!isFno && allIspsAccess">
        <v-checkbox v-model="allIsps" :label="'All my ISPs (' + allIspsCount + ')'" hide-details @change="loadFaults"></v-checkbox>
      </v-flex>
      <v-flex xs12 sm8>
        <v-text-field v-model="search" append-icon="search" label="Search" single-line hide-details></v-text-field>
      </v-flex>
    </v-layout>

    <!-- Report controls. FNO only. The range drives the table, the tiles and
         the export together, so what downloads is always what is on screen. -->
    <v-layout row wrap align-center v-if="isFno" class="mb-2">
      <v-flex xs6 sm3 class="pr-2">
        <v-text-field v-model="dateFrom" type="date" label="From" hide-details></v-text-field>
      </v-flex>
      <v-flex xs6 sm3 class="pr-2">
        <v-text-field v-model="dateTo" type="date" label="To" hide-details></v-text-field>
      </v-flex>
      <v-flex xs12 sm6 class="text-xs-right mt-2">
        <v-btn small flat @click="thisMonth">This month</v-btn>
        <v-btn small flat @click="dateFrom = ''; dateTo = ''" :disabled="!dateFrom && !dateTo">Clear</v-btn>
        <v-btn small color="primary" @click="exportCsv" :disabled="filteredFaults.length === 0">Export CSV</v-btn>
      </v-flex>
    </v-layout>

    <!-- SLA tiles. FNO only - the ISP never sees these numbers. Counts cover
         every fault in the list, not just what the search box is showing. -->
    <v-layout row wrap v-if="isFno" class="mb-2">
      <v-flex xs6 sm3 class="pa-1">
        <v-card color="blue" dark class="text-xs-center pa-2 sla-tile">
          <div class="subheading font-weight-bold">Open</div>
          <div class="headline">{{ slaCounts.open }}</div>
        </v-card>
      </v-flex>
      <v-flex xs6 sm3 class="pa-1">
        <v-card color="orange" dark class="text-xs-center pa-2 sla-tile">
          <div class="subheading font-weight-bold">At Risk</div>
          <div class="headline">{{ slaCounts.atRisk }}</div>
        </v-card>
      </v-flex>
      <v-flex xs6 sm3 class="pa-1">
        <v-card color="red" dark class="text-xs-center pa-2 sla-tile">
          <div class="subheading font-weight-bold">Breached</div>
          <div class="headline">{{ slaCounts.breached }}</div>
        </v-card>
      </v-flex>
      <v-flex xs6 sm3 class="pa-1">
        <v-card color="green" dark class="text-xs-center pa-2 sla-tile">
          <div class="subheading font-weight-bold">Met</div>
          <div class="headline">{{ slaCounts.met }}</div>
        </v-card>
      </v-flex>
    </v-layout>

    <v-data-table :headers="tableHeaders" :items="filteredFaults" :search="search" :loading="loading" class="elevation-1">
      <template slot="items" slot-scope="props">
        <td>{{ props.item.reference }}</td>
        <td>{{ props.item.title }}</td>
        <td v-if="isFno || allIsps">{{ props.item.ispName || (props.item.customerOrganisationId ? '-' : 'Own fault') }}</td>
        <td>{{ props.item.unitReference }}</td>
        <td>{{ estateFor(props.item) }}</td>
        <td>
          <v-chip small :color="statusColour(props.item.status)" text-color="white">{{ props.item.status }}</v-chip>
          <div v-if="isFno && props.item.contractorName && props.item.referralStatus !== 'Withdrawn'" class="caption">
            {{ props.item.contractorName }} &middot; {{ props.item.referralStatus }}
          </div>
          <div v-if="!isFno && props.item.technicianDispatchedAt" class="caption">Technician dispatched</div>
        </td>
        <td v-if="isFno">
          <v-chip v-if="slaLabel(props.item)" small :color="slaColour(slaLabel(props.item))" text-color="white">{{ slaLabel(props.item) }}</v-chip>
          <span v-else class="grey--text">-</span>
        </td>
        <td>{{ formatDate(props.item.createdAt) }}</td>
        <td class="text-xs-right">
          <v-btn flat small color="primary" @click="openDetail(props.item)">Open</v-btn>
        </td>
      </template>
      <template slot="no-data">
        <div class="text-xs-center pa-3 grey--text">No faults logged.</div>
      </template>
    </v-data-table>

    <!-- log a fault -->
    <v-dialog v-model="logDialog" max-width="640px" persistent>
      <v-card>
        <v-card-title class="title">{{ isFno ? 'Log own fault' : 'Log a fault' }}</v-card-title>
        <v-card-text>
          <v-select :items="networks" item-text="name" item-value="id"
                    v-model="form.locationNetworkId" label="Estate *"
                    :loading="loadingNetworks" @change="loadUnits"
                    no-data-text="No estates found for your organisation"></v-select>
          <v-text-field v-model="form.title" label="Summary *" maxlength="200"></v-text-field>
          <v-select :items="categories" v-model="form.category" label="Category"></v-select>
          <v-combobox :items="unitItems" v-model="unitPick"
                      :label="isFno ? 'Unit' : 'Unit *'" :disabled="!form.locationNetworkId"
                      :loading="loadingUnits" @change="onUnitChange"
                      hint="Pick a unit, or type one that is not listed"
                      persistent-hint
                      no-data-text="No units on this estate"></v-combobox>
          <v-text-field v-model="form.circuitNumber" label="Circuit number"></v-text-field>
          <v-textarea v-model="form.description" label="What is wrong?" rows="4"></v-textarea>
          <v-text-field v-model="form.contactName" label="Site contact name"></v-text-field>
          <v-text-field v-model="form.contactNumber" label="Site contact number"></v-text-field>
          <div class="mt-3">
            <input ref="logFileInput" type="file" accept="image/*" multiple style="display:none"
                   @change="onPickFiles($event, 'log')">
            <v-btn small flat color="primary" @click="$refs.logFileInput.click()">
              <v-icon left small>attach_file</v-icon>Add photos
            </v-btn>
            <span v-if="!pendingFiles.length" class="grey--text caption">Optional</span>
            <v-chip v-for="(f, i) in pendingFiles" :key="i" small close
                    @input="pendingFiles.splice(i, 1)">{{ f.name }}</v-chip>
          </div>
          <div class="red--text body-1" v-if="error">{{ error }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="logDialog = false" :disabled="saving">Cancel</v-btn>
          <v-btn color="primary" @click="saveFault" :loading="saving">Log fault</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- detail -->
    <v-dialog v-model="detailDialog" max-width="760px">
      <v-card v-if="selected">
        <v-card-title class="title">
          {{ selected.reference }} &mdash; {{ selected.title }}
          <v-spacer></v-spacer>
          <v-chip small :color="statusColour(selected.status)" text-color="white">{{ selected.status }}</v-chip>
        </v-card-title>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12 sm6 v-if="isFno || allIsps"><strong>ISP:</strong> {{ selected.ispName || (selected.customerOrganisationId ? '-' : 'Own fault (no ISP)') }}</v-flex>
            <v-flex xs12 v-if="!isFno && selected.technicianDispatchedAt" class="mt-1">
              <v-chip small color="blue" text-color="white">Technician dispatched</v-chip>
              <span class="caption grey--text">{{ formatDate(selected.technicianDispatchedAt) }}</span>
            </v-flex>
            <v-flex xs12 v-if="isFno && selected.slaAckStatus" class="mt-2">
              <strong>Acknowledge:</strong>
              <v-chip small :color="slaColour(selected.slaAckStatus)" text-color="white">{{ selected.slaAckStatus }}</v-chip>
              <span class="caption grey--text">{{ selected.slaAckPercent }}% of {{ selected.slaAckHours }}h &middot; due {{ formatDate(selected.slaAckDueAt) }}<span v-if="selected.slaAckAt"> &middot; picked up {{ formatDate(selected.slaAckAt) }}</span></span>
            </v-flex>
            <v-flex xs12 v-if="isFno && selected.slaRestoreStatus">
              <strong>Restore:</strong>
              <v-chip small :color="slaColour(selected.slaRestoreStatus)" text-color="white">{{ selected.slaRestoreStatus }}</v-chip>
              <span class="caption grey--text">{{ selected.slaRestorePercent }}% of {{ selected.slaRestoreHours }}h &middot; due {{ formatDate(selected.slaRestoreDueAt) }}<span v-if="selected.slaRestoreAt"> &middot; resolved {{ formatDate(selected.slaRestoreAt) }}</span></span>
            </v-flex>
            <v-flex xs12 sm6><strong>Estate:</strong> {{ estateName }}</v-flex>
            <v-flex xs12 sm6><strong>Unit:</strong> {{ selected.unitReference || '-' }}</v-flex>
            <v-flex xs12 sm6><strong>Circuit:</strong> {{ selected.circuitNumber || '-' }}</v-flex>
            <v-flex xs12 v-if="isFno && selected.deviceId" class="mt-2">
              <v-layout row wrap align-center>
                <v-flex xs12 sm3><strong>ONU status</strong></v-flex>
                <v-flex xs12 sm9>
                  <v-progress-circular v-if="onu.loading" indeterminate size="20" color="grey"></v-progress-circular>
                  <span v-else-if="onu.error" class="grey--text caption">{{ onu.error }}</span>
                  <span v-else-if="onu.status">
                    <v-chip small :color="onu.status.isOnline ? 'green' : 'red'" text-color="white">{{ onu.status.isOnline ? 'Online' : 'Offline' }}</v-chip>
                    <strong class="ml-2">RX:</strong> {{ formatDbm(onu.status.rxPower) }}
                    <strong class="ml-2">RSSI:</strong> {{ formatDbm(onu.status.opticRssi) }}
                  </span>
                  <span v-else class="grey--text caption">Not checked.</span>
                  <v-btn small flat color="primary" :loading="onu.loading" @click="loadOnuStatus()">Re-check</v-btn>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex xs12 sm6><strong>Package:</strong>
              <span v-if="selected.packageName || selected.packageCode || selected.downloadMbps">{{ selected.packageName || selected.packageCode || '-' }}<span v-if="selected.downloadMbps"> &middot; {{ selected.downloadMbps }}/{{ selected.uploadMbps }} Mbps</span></span>
              <span v-else>-</span>
            </v-flex>
            <v-flex xs12 sm6><strong>Category:</strong> {{ selected.category || '-' }}</v-flex>
            <v-flex xs12 sm6><strong>Logged:</strong> {{ formatDate(selected.createdAt) }}<span v-if="selected.loggedByName"> &middot; {{ selected.loggedByName }}</span></v-flex>
            <v-flex xs12 sm6><strong>Site contact:</strong> {{ selected.contactName || '-' }} {{ selected.contactNumber }}</v-flex>
            <v-flex xs12 class="mt-2"><strong>Description</strong><div>{{ selected.description || '-' }}</div></v-flex>
            <v-flex xs12 v-if="selected.resolutionNote" class="mt-2">
              <strong>Resolution</strong><div>{{ selected.resolutionNote }}</div>
            </v-flex>
          </v-layout>

          <v-divider class="my-3"></v-divider>

          <div>
            <strong>Photos</strong>
            <div class="mt-2">
              <span v-if="!attachments.length" class="grey--text caption">None attached.</span>
              <v-avatar v-for="a in attachments" :key="a.id" tile size="96" class="mr-2 mb-2"
                        style="cursor:pointer;border:1px solid #ddd"
                        @click="openAttachment(a)">
                <img v-if="thumbs[a.id]" :src="thumbs[a.id]" style="object-fit:cover">
                <v-progress-circular v-else indeterminate size="20" color="grey"></v-progress-circular>
              </v-avatar>
            </div>
            <input ref="detailFileInput" type="file" accept="image/*" multiple style="display:none"
                   @change="onPickFiles($event, 'detail')">
            <v-btn small flat color="primary" :loading="uploading" @click="$refs.detailFileInput.click()">
              <v-icon left small>attach_file</v-icon>Add photos
            </v-btn>
          </div>

          <v-divider class="my-3"></v-divider>

          <div v-if="isFno">
            <v-layout row wrap align-center>
              <v-flex xs12 sm5>
                <v-select :items="statuses" v-model="selected.status" label="Status"></v-select>
              </v-flex>
              <v-flex xs12 sm5>
                <v-text-field v-model="selected.resolutionNote" label="Resolution note"></v-text-field>
              </v-flex>
              <v-flex xs12 sm2 class="text-sm-right">
                <v-btn color="primary" small @click="saveStatus" :loading="savingStatus">Save</v-btn>
              </v-flex>
            </v-layout>
            <v-divider class="my-3"></v-divider>

            <!-- Contractor referral. FNO only: the ISP sees "Technician
                 dispatched" and nothing else of this. -->
            <strong>Contractor</strong>
            <div v-if="ref.loading" class="grey--text caption mt-1">Loading&hellip;</div>
            <div v-else>
              <div v-if="ref.current" class="mt-1">
                <v-chip small :color="referralColour(ref.current.status)" text-color="white">{{ ref.current.status }}</v-chip>
                {{ ref.current.contractorName }}<span v-if="ref.current.assignedName"> &middot; {{ ref.current.assignedName }}</span>
                <span class="caption grey--text">&middot; referred {{ formatDate(ref.current.createdAt) }}<span v-if="ref.current.referredByName"> by {{ ref.current.referredByName }}</span></span>
                <div v-if="ref.current.note" class="caption">Instructions: {{ ref.current.note }}</div>
                <div v-if="ref.current.contractorNote" class="caption">Contractor: {{ ref.current.contractorNote }}</div>
                <div v-if="ref.current.status === 'Completed'" class="caption green--text">
                  The contractor has finished. Check with the client, then resolve the fault above.
                </div>
              </div>
              <div v-else class="grey--text caption mt-1">
                Not referred<span v-if="lastReturned"> &middot; {{ lastReturned.contractorName }} returned it: {{ lastReturned.contractorNote }}</span>
              </div>
              <v-layout row wrap align-center v-if="selected.status !== 'Resolved'">
                <v-flex xs12 sm3>
                  <v-select :items="contractors" item-text="name" item-value="id" v-model="referTo"
                            label="Contractor" no-data-text="No contractors set up" @change="loadTechnicians"></v-select>
                </v-flex>
                <v-flex xs12 sm3>
                  <v-select :items="technicianItems" item-text="name" item-value="id" v-model="referTech"
                            label="Technician" :disabled="!referTo" :loading="loadingTechs"></v-select>
                </v-flex>
                <v-flex xs12 sm3>
                  <v-text-field v-model="referNote" label="Instructions for the contractor" maxlength="1000"></v-text-field>
                </v-flex>
                <v-flex xs12 sm3 class="text-sm-right">
                  <v-btn small color="primary" :disabled="!referTo" :loading="referring" @click="referFault">
                    {{ ref.current && isOpenReferral(ref.current) ? 'Re-refer' : 'Refer' }}
                  </v-btn>
                  <v-btn small flat color="error" v-if="ref.current && isOpenReferral(ref.current)"
                         :loading="withdrawing" @click="withdrawReferral">Withdraw</v-btn>
                </v-flex>
              </v-layout>
            </div>
            <v-divider class="my-3"></v-divider>
          </div>

          <div v-else-if="selected.status === 'Resolved'">
            <strong>Still not resolved?</strong>
            <p class="grey--text caption mb-1">
              Reopen the fault if the problem is still there. Purple Forest will be notified.
            </p>
            <v-layout row wrap align-center>
              <v-flex xs12 sm9>
                <v-text-field v-model="reopenReason" maxlength="500"
                              label="What is still wrong? *"></v-text-field>
              </v-flex>
              <v-flex xs12 sm3 class="text-sm-right">
                <v-btn color="error" small :loading="reopening" :disabled="!reopenReason"
                       @click="reopenFault">Reopen</v-btn>
              </v-flex>
            </v-layout>
            <v-divider class="my-3"></v-divider>
          </div>

          <v-tabs v-model="detailTab" grow slider-color="primary" class="mb-2">
            <v-tab>Comments</v-tab>
            <v-tab>History ({{ history.length }})</v-tab>
            <v-tab v-if="isFno">Contractor ({{ ref.comments.length }})</v-tab>
          </v-tabs>
          <div v-show="detailTab === 0">
          <div v-if="comments.length === 0" class="grey--text body-1 mb-2">No comments yet.</div>
          <div v-for="c in comments" :key="c.id" class="mb-2">
            <div class="caption grey--text">
              {{ c.authorSide === 'FNO' ? 'Network operator' : 'ISP' }}
              <span v-if="c.authorName">&middot; {{ c.authorName }}</span>
              &middot; {{ formatDate(c.createdAt) }}
            </div>
            <div>{{ c.comment }}</div>
          </div>
          <v-textarea v-model="newComment" label="Add a comment" rows="2"></v-textarea>
          <v-btn small color="primary" @click="addComment" :loading="savingComment" :disabled="!newComment">Post</v-btn>
          </div>
          <div v-show="detailTab === 1">
            <div v-if="historyLoading" class="grey--text body-1">Loading&hellip;</div>
            <div v-else-if="history.length === 0" class="grey--text body-1">No other faults for this client.</div>
            <div v-else>
              <div v-for="h in history" :key="h.id" class="mb-3">
                <div class="caption grey--text">
                  {{ h.reference }} &middot; {{ formatDate(h.createdAt) }}
                  <span v-if="h.matchNote">&middot; {{ h.matchNote }}</span>
                </div>
                <div>
                  <v-chip small :color="statusColour(h.status)" text-color="white">{{ h.status }}</v-chip>
                  <strong>{{ h.title }}</strong>
                  <span v-if="h.category" class="grey--text">&middot; {{ h.category }}</span>
                </div>
                <div v-if="h.resolutionNote" class="caption grey--text">{{ h.resolutionNote }}</div>
              </div>
            </div>
          </div>
          <div v-if="isFno" v-show="detailTab === 2">
            <div class="caption grey--text mb-2">Private between you and the contractor. The ISP never sees this.</div>
            <div v-if="ref.comments.length === 0" class="grey--text body-1 mb-2">No messages yet.</div>
            <div v-for="c in ref.comments" :key="c.id" class="mb-2">
              <div class="caption grey--text">
                {{ c.authorSide === 'System' ? 'Status update' : (c.authorSide === 'FNO' ? 'Network operator' : 'Contractor') }}
                &middot; {{ c.contractorName }}
                <span v-if="c.authorName">&middot; {{ c.authorName }}</span>
                &middot; {{ formatDate(c.createdAt) }}
              </div>
              <div :class="c.authorSide === 'System' ? 'grey--text' : ''">{{ c.comment }}</div>
            </div>
            <div class="mt-2">
              <span v-if="!ref.attachments.length" class="grey--text caption">No contractor photos.</span>
              <v-avatar v-for="a in ref.attachments" :key="a.id" tile size="96" class="mr-2 mb-2"
                        style="cursor:pointer;border:1px solid #ddd"
                        @click="openContractorAttachment(a)">
                <img v-if="cThumbs[a.id]" :src="cThumbs[a.id]" style="object-fit:cover">
                <v-progress-circular v-else indeterminate size="20" color="grey"></v-progress-circular>
              </v-avatar>
            </div>
            <div v-if="ref.referrals.length">
              <v-textarea v-model="newPrivate" :label="'Message to ' + (latestContractorName || 'the contractor')" rows="2"></v-textarea>
              <v-btn small color="primary" @click="addPrivateComment" :loading="savingPrivate" :disabled="!newPrivate">Post</v-btn>
              <input ref="privateFileInput" type="file" accept="image/*" multiple style="display:none"
                     @change="onPickPrivateFiles($event)">
              <v-btn small flat color="primary" :loading="uploadingPrivate" @click="$refs.privateFileInput.click()">
                <v-icon left small>attach_file</v-icon>Add photos
              </v-btn>
              <div class="mt-3"><strong>Referral history</strong></div>
              <div v-for="r in ref.referrals" :key="r.id" class="caption">
                {{ formatDate(r.createdAt) }} &middot; {{ r.contractorName }} &middot; {{ r.status }}<span v-if="r.referredByName"> &middot; by {{ r.referredByName }}</span>
              </div>
            </div>
            <div v-else class="grey--text caption">Refer the fault to a contractor to start a thread.</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="detailDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewer" max-width="900px">
      <v-card>
        <v-card-text class="text-xs-center pa-2">
          <img :src="viewerSrc" style="max-width:100%;max-height:78vh">
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="viewer = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack" :timeout="4000">{{ snackText }}</v-snackbar>
  </v-container>
</template>

<script>
import Api from "@/services/Api";

export default {
  name: "IspFaults",
  data() {
    return {
      faults: [],
      // "All my ISPs": one list across every ISP the user belongs to (server allow-list).
      allIsps: false,
      allIspsAccess: false,
      allIspsCount: 0,
      networks: [],
      units: [],
      unitPick: null,
      attachments: [],
      thumbs: {},
      pendingFiles: [],
      uploading: false,
      viewer: false,
      viewerSrc: "",
      reopenReason: "",
      reopening: false,
      comments: [],
      selected: null,
      search: "",
      loading: false,
      loadingNetworks: false,
      loadingUnits: false,
      saving: false,
      savingStatus: false,
      savingComment: false,
      logDialog: false,
      detailDialog: false,
      newComment: "",
      error: "",
      snack: false,
      snackText: "",
      statusFilter: "All",
      statusFilters: ["All", "Open", "In Progress", "Resolved"],
      statuses: ["Open", "In Progress", "Resolved"],
      categories: [
        "No connection",
        "Slow speed",
        "Intermittent connection",
        "Physical damage",
        "Power",
        "Other"
      ],
      headers: [
        { text: "Reference", value: "reference" },
        { text: "Summary", value: "title" },
        { text: "Unit", value: "unitReference" },
        { text: "Estate", value: "locationNetworkId", sortable: false },
        { text: "Status", value: "status" },
        { text: "Logged", value: "createdAt" },
        { text: "", value: "id", sortable: false, align: "right" }
      ],
      onu: { loading: false, error: null, status: null },
      pollTimer: null,
      detailTab: 0,
      history: [],
      historyLoading: false,
      dateFrom: "",
      dateTo: "",
      form: {},
      // Contractor referrals (FNO only).
      contractors: [],
      referTo: null,
      referNote: "",
      // Optional: the contractor's technician for the job ("Any technician" = null).
      referTech: null,
      technicians: [],
      loadingTechs: false,
      referring: false,
      withdrawing: false,
      ref: { loading: false, referrals: [], current: null, comments: [], attachments: [] },
      cThumbs: {},
      newPrivate: "",
      savingPrivate: false,
      uploadingPrivate: false
    };
  },
  computed: {
    isFno() {
      return this.$route.path === "/ispFaultQueue";
    },
    // The ISP column only makes sense on the FNO queue - on the ISP's own
    // page every row is theirs.
    tableHeaders() {
      if (!this.isFno) {
        if (!this.allIsps) return this.headers;
        const hi = this.headers.slice();
        hi.splice(2, 0, { text: "ISP", value: "ispName" });
        return hi;
      }
      const h = this.headers.slice();
      h.splice(2, 0, { text: "ISP", value: "ispName" });
      // SLA sits beside Status - both answer "how is this fault doing". The
      // ISP never sees it: the flags are Purple Forest's own measure.
      const at = h.findIndex(x => x.value === "status");
      h.splice(at + 1, 0, { text: "SLA", value: "slaRestoreStatus", sortable: false });
      return h;
    },
    // One date range for the whole page. Empty dates mean no filtering, which
    // is what the ISP side always gets - it has no way to set them.
    filteredFaults() {
      const list = this.faults || [];
      if (!this.dateFrom && !this.dateTo) return list;
      const from = this.dateFrom ? new Date(this.dateFrom + "T00:00:00") : null;
      const to = this.dateTo ? new Date(this.dateTo + "T23:59:59") : null;
      return list.filter(f => {
        const d = new Date(f.createdAt);
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      });
    },

    // The tiles above the queue. Counted off the list already in memory rather
    // than a second endpoint. Open is a status question; the other three are
    // the SLA verdict, so a fault resolved late still counts as Breached.
    slaCounts() {
      const out = { open: 0, atRisk: 0, breached: 0, met: 0 };
      (this.filteredFaults || []).forEach(f => {
        if (f.status !== "Resolved") out.open++;
        const label = this.slaLabel(f);
        if (label === "At risk") out.atRisk++;
        else if (label === "Breached") out.breached++;
        else if (label === "Met") out.met++;
      });
      return out;
    },

    // Both sides fill `networks` with {id, name} - the ISP from its own
    // estates, the FNO from the networks it owns - so one lookup serves both.
    estateName() {
      if (this.selected && this.selected.estateName) return this.selected.estateName;
      const id = this.selected && this.selected.locationNetworkId;
      if (!id) return "-";
      const n = (this.networks || []).find(x => x.id === id);
      return (n && n.name) || "-";
    },
    // The FNO's own-fault picker lists every unit on the estate (with the live
    // ISP, if any), so it is keyed on the location rather than an account.
    unitItems() {
      return this.units.map(u => ({
        text:
          (u.unitNo ? "Unit " + u.unitNo : "No unit number") +
          (this.isFno && u.ispName ? "  \u00b7  " + u.ispName : "") +
          (u.circuitNumber ? "  \u00b7  " + u.circuitNumber : ""),
        value: this.isFno ? "L" + u.locationId : u.serviceAccountId,
        raw: u
      }));
    },
    technicianItems() {
      return [{ id: null, name: "Any technician" }].concat(this.technicians);
    },
    lastReturned() {
      const list = (this.ref.referrals || []).filter(r => r.status === "Returned");
      return list.length ? list[list.length - 1] : null;
    },
    latestContractorName() {
      const list = this.ref.referrals || [];
      return list.length ? list[list.length - 1].contractorName : "";
    }
  },
  mounted() {
    this.loadFaults();
    this.loadNetworks();
    if (this.isFno) this.loadContractors();
    if (!this.isFno) this.loadAllIspsAccess();
    this.startPolling();
    document.addEventListener("visibilitychange", this.onVisibility);
  },
  beforeDestroy() {
    this.stopPolling();
    document.removeEventListener("visibilitychange", this.onVisibility);
  },
  methods: {
    // The queue gets left open all day, so it refreshes itself. Polling pauses
    // while the tab is hidden, while a request is already in flight, and while
    // a dialog is open - the table must not reshuffle under someone reading it.
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(() => {
        if (document.visibilityState !== "visible") return;
        if (this.loading || this.detailDialog || this.logDialog) return;
        this.loadFaults();
      }, 60000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    onVisibility() {
      if (document.visibilityState !== "visible") return;
      if (this.loading || this.detailDialog || this.logDialog) return;
      this.loadFaults();
    },

    endpoint() {
      if (this.isFno) return "/ispFault";
      return this.allIsps ? "/ispFault/allMyIsps" : "/ispFault/mine";
    },
    loadAllIspsAccess() {
      Api()
        .get("/ispFault/allMyIsps/access")
        .then(response => {
          this.allIspsAccess = !!response.data.enabled;
          this.allIspsCount = (response.data.organisations || []).length;
        })
        .catch(() => {});
    },
    // Calls about the open fault. In "All my ISPs" the fault may belong to another
    // of the user's ISPs, so they go out as that ISP (its token from the switcher
    // list); the server then applies its usual checks for that ISP.
    faultApi() {
      const api = Api();
      const f = this.selected;
      if (this.allIsps && f && f.customerOrganisationId) {
        const o = (this.$store.state.organisations || []).find(x => x.org && x.org.id === f.customerOrganisationId);
        if (o && o.org.token) api.defaults.headers.apikey = o.org.token;
      }
      return api;
    },
    loadFaults() {
      this.loading = true;
      let url = this.endpoint();
      if (this.statusFilter && this.statusFilter !== "All") {
        url = url + "?status=" + encodeURIComponent(this.statusFilter);
      }
      Api()
        .get(url)
        .then(response => {
          this.faults = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.notify("Could not load faults.");
        });
    },
    loadNetworks() {
      this.loadingNetworks = true;
      Api()
        .get(this.isFno ? "/locationNetworks/basicActive" : "/ispFault/networks")
        .then(response => {
          this.networks = response.data;
          this.loadingNetworks = false;
        })
        .catch(() => {
          this.loadingNetworks = false;
          this.notify("Could not load estates.");
        });
    },
    loadUnits() {
      this.units = [];
      this.unitPick = null;
      this.form.serviceAccountId = null;
      this.form.unitReference = "";
      this.form.circuitNumber = "";
      if (!this.form.locationNetworkId) return;
      this.loadingUnits = true;
      Api()
        .get((this.isFno ? "/faultReferral/units/" : "/ispFault/units/") + this.form.locationNetworkId)
        .then(response => {
          this.units = response.data;
          this.loadingUnits = false;
        })
        .catch(() => {
          this.loadingUnits = false;
          this.notify("Could not load units.");
        });
    },
    onUnitChange(value) {
      if (value && typeof value === "object") {
        const r = value.raw || {};
        this.form.locationId = r.locationId || null;
        this.form.serviceAccountId = r.serviceAccountId || null;
        this.form.unitReference = r.unitNo || "";
        this.form.circuitNumber = r.circuitNumber || "";
        this.form.deviceId = r.deviceId || null;
        if (r.contactName) this.form.contactName = r.contactName;
        if (r.contactNumber) this.form.contactNumber = r.contactNumber;
      } else {
        this.form.locationId = null;
        this.form.serviceAccountId = null;
        this.form.deviceId = null;
        this.form.unitReference = value ? String(value).trim() : "";
      }
    },
    openLog() {
      this.error = "";
      this.units = [];
      this.unitPick = null;
      this.pendingFiles = [];
      this.form = {
        locationNetworkId: null,
        locationId: null,
        serviceAccountId: null,
        deviceId: null,
        title: "",
        category: null,
        unitReference: "",
        circuitNumber: "",
        description: "",
        contactName: "",
        contactNumber: ""
      };
      this.logDialog = true;
    },
    saveFault() {
      if (!this.form.locationNetworkId || !this.form.title) {
        this.error = "An estate and a summary are required.";
        return;
      }
      if (!this.isFno && !this.form.serviceAccountId && !String(this.form.unitReference || "").trim()) {
        this.error = "Select the unit, or type the unit number.";
        return;
      }
      this.error = "";
      this.saving = true;
      Api()
        .post(this.isFno ? "/faultReferral/ownFault" : "/ispFault", this.form)
        .then(async response => {
          const id = response.data && response.data.id;
          if (id && this.pendingFiles.length) {
            for (const p of this.pendingFiles) {
              await this.uploadOne(id, p);
            }
          }
          this.saving = false;
          this.logDialog = false;
          this.notify("Fault logged.");
          this.loadFaults();
        })
        .catch(error => {
          this.saving = false;
          this.error =
            (error.response && error.response.data && error.response.data.error) ||
            "Could not log the fault.";
        });
    },
    // Everything that ever happened to this client, from two angles: faults on
    // the same service account, and faults at the same unit on the same estate
    // (a previous ISP or a recreated account). Labelled so the two are not
    // confused. Filtered client-side off the full list - fine at current volume,
    // worth a dedicated endpoint once this runs to thousands of faults.
    loadHistory() {
      const sel = this.selected;
      if (!sel) return;
      this.history = [];
      this.historyLoading = true;
      Api()
        .get(this.endpoint())
        .then(res => {
          const all = Array.isArray(res.data) ? res.data : [];
          const out = [];
          all.forEach(f => {
            if (f.id === sel.id) return;
            const sameAccount =
              sel.serviceAccountId && f.serviceAccountId === sel.serviceAccountId;
            const sameUnit =
              sel.unitReference &&
              f.unitReference === sel.unitReference &&
              sel.locationNetworkId &&
              f.locationNetworkId === sel.locationNetworkId;
            if (sameAccount) {
              out.push(Object.assign({}, f, { matchNote: "this service" }));
            } else if (sameUnit) {
              out.push(Object.assign({}, f, { matchNote: "previous service at this unit" }));
            }
          });
          out.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          this.history = out;
          this.historyLoading = false;
        })
        .catch(() => {
          this.historyLoading = false;
        });
    },

    // Estate for a row. Both sides fill `networks` with {id, name} - the FNO
    // from the networks it owns, the ISP from the estates it sells on - so the
    // same lookup serves the table and the detail panel.
    estateFor(item) {
      if (item && item.estateName) return item.estateName;
      const id = item && item.locationNetworkId;
      if (!id) return "-";
      const n = (this.networks || []).find(x => x.id === id);
      return (n && n.name) || "-";
    },

    formatDbm(v) {
      return (v === null || v === undefined || isNaN(v)) ? "-" : v.toFixed(2) + " dBm";
    },

    // ONU state for the FNO queue. The fault carries deviceId; the device
    // carries Handle_ID, which is what the Zhone API keys on.
    loadOnuStatus() {
      if (!this.isFno) return;
      const deviceId = this.selected && this.selected.deviceId;
      if (!deviceId) return;
      this.onu = { loading: true, error: null, status: null };
      Api()
        .get("/devices/" + deviceId)
        .then(res => {
          const body = res.data || {};
          const handle = body.Handle_ID || (body.device && body.device.Handle_ID);
          if (!handle) {
            this.onu = { loading: false, error: "No ONU handle on this device.", status: null };
            return;
          }
          return Api()
            .get("/zhone/onuStatus/" + handle)
            .then(r => {
              const d = (r.data && r.data.data) || {};
              const num = v => { const n = parseFloat(v); return isNaN(n) ? null : n / 10; };
              const online =
                d.onuStatusWord === "00000000000000000000001" ||
                d.onuStatusWord === "01000000000000000000001";
              this.onu = {
                loading: false,
                error: null,
                status: {
                  isOnline: r.data && r.data.code === 200 ? online : false,
                  rxPower: num(d.rxPower),
                  opticRssi: num(d.opticRssi)
                }
              };
            });
        })
        .catch(() => {
          this.onu = { loading: false, error: "Could not reach the OLT.", status: null };
        });
    },

    openDetail(item) {
      this.selected = Object.assign({}, item);
      this.comments = [];
      this.attachments = [];
      this.reopenReason = "";
      this.thumbs = {};
      this.newComment = "";
      this.detailDialog = true;
      this.loadComments();
      this.loadAttachments();
      this.loadOnuStatus();
      this.detailTab = 0;
      this.loadHistory();
      this.referTo = null;
      this.referNote = "";
      this.newPrivate = "";
      this.cThumbs = {};
      this.ref = { loading: false, referrals: [], current: null, comments: [], attachments: [] };
      if (this.isFno) this.loadReferral();
    },

    // ---- contractor referrals (FNO only) ----
    loadContractors() {
      Api()
        .get("/faultContractors")
        .then(response => {
          this.contractors = response.data || [];
        })
        .catch(() => {});
    },
    loadReferral() {
      if (!this.selected) return;
      const faultId = this.selected.id;
      this.ref.loading = true;
      Api()
        .get("/faultReferral/" + faultId)
        .then(response => {
          if (!this.selected || this.selected.id !== faultId) return;
          const d = response.data || {};
          this.ref = {
            loading: false,
            referrals: d.referrals || [],
            current: d.current || null,
            comments: d.comments || [],
            attachments: d.attachments || []
          };
          this.ref.attachments.forEach(a => this.loadCThumb(faultId, a.id));
        })
        .catch(() => {
          this.ref.loading = false;
          this.notify("Could not load the contractor details.");
        });
    },
    isOpenReferral(r) {
      return ["Referred", "Accepted", "On site"].indexOf(r && r.status) !== -1;
    },
    referralColour(status) {
      if (status === "Completed") return "green";
      if (status === "Returned") return "red";
      if (status === "On site") return "orange";
      return "blue";
    },
    loadTechnicians() {
      this.referTech = null;
      this.technicians = [];
      if (!this.referTo) return;
      const id = this.referTo;
      this.loadingTechs = true;
      Api()
        .get("/faultContractors/" + id + "/technicians")
        .then(response => {
          if (id !== this.referTo) return;
          this.technicians = response.data || [];
          this.loadingTechs = false;
        })
        .catch(() => {
          this.loadingTechs = false;
        });
    },
    referFault() {
      if (!this.referTo) return;
      this.referring = true;
      Api()
        .post("/faultReferral/" + this.selected.id + "/refer", {
          contractorOrganisationId: this.referTo,
          technicianContactId: this.referTech,
          note: this.referNote
        })
        .then(() => {
          this.referring = false;
          this.referTo = null;
          this.referTech = null;
          this.technicians = [];
          this.referNote = "";
          if (this.selected.status === "Open") this.selected.status = "In Progress";
          this.notify("Fault referred.");
          this.loadReferral();
          this.loadFaults();
        })
        .catch(error => {
          this.referring = false;
          this.notify(
            (error.response && error.response.data && error.response.data.error) ||
              "Could not refer the fault."
          );
        });
    },
    withdrawReferral() {
      if (!confirm("Withdraw the fault from " + (this.ref.current && this.ref.current.contractorName) + "?")) return;
      this.withdrawing = true;
      Api()
        .post("/faultReferral/" + this.selected.id + "/withdraw", {})
        .then(() => {
          this.withdrawing = false;
          this.notify("Referral withdrawn.");
          this.loadReferral();
          this.loadFaults();
        })
        .catch(() => {
          this.withdrawing = false;
          this.notify("Could not withdraw the referral.");
        });
    },
    addPrivateComment() {
      if (!this.newPrivate) return;
      this.savingPrivate = true;
      Api()
        .post("/faultReferral/" + this.selected.id + "/comments", { comment: this.newPrivate })
        .then(() => {
          this.savingPrivate = false;
          this.newPrivate = "";
          this.loadReferral();
        })
        .catch(error => {
          this.savingPrivate = false;
          this.notify(
            (error.response && error.response.data && error.response.data.error) ||
              "Could not post the message."
          );
        });
    },
    async onPickPrivateFiles(event) {
      const files = Array.prototype.slice.call(event.target.files || []);
      event.target.value = "";
      if (!files.length) return;
      this.uploadingPrivate = true;
      for (const f of files.filter(x => x.size <= 25 * 1024 * 1024)) {
        try {
          const data = await this.resizeImage(f);
          await Api().post("/faultReferral/" + this.selected.id + "/attachments", { name: f.name, data: data });
        } catch (e) {
          this.notify("Could not upload " + f.name);
        }
      }
      this.uploadingPrivate = false;
      this.loadReferral();
    },
    loadCThumb(faultId, attachmentId) {
      Api()
        .get("/faultReferral/" + faultId + "/attachments/" + attachmentId + "?kind=contractor")
        .then(response => {
          this.$set(this.cThumbs, attachmentId, response.data);
        })
        .catch(() => {});
    },
    openContractorAttachment(a) {
      const src = this.cThumbs[a.id];
      if (!src) return;
      this.viewerSrc = src;
      this.viewer = true;
    },

    // Photos are resized in the browser before upload: longest edge 1600px,
    // JPEG 0.8. A phone photo lands around 300 KB instead of 4 MB, which keeps
    // uploads quick on site and disk growth sane.
    resizeImage(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("read failed"));
        reader.onload = () => {
          const img = new Image();
          img.onerror = () => reject(new Error("decode failed"));
          img.onload = () => {
            const MAX = 1600;
            let w = img.width;
            let h = img.height;
            if (w > MAX || h > MAX) {
              const s = Math.min(MAX / w, MAX / h);
              w = Math.round(w * s);
              h = Math.round(h * s);
            }
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            canvas.getContext("2d").drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL("image/jpeg", 0.8));
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(file);
      });
    },
    async onPickFiles(event, mode) {
      const files = Array.prototype.slice.call(event.target.files || []);
      event.target.value = "";
      if (!files.length) return;
      const usable = files.filter(f => f.size <= 25 * 1024 * 1024);
      if (usable.length !== files.length) this.notify("Files over 25 MB were skipped.");
      const prepared = [];
      for (const f of usable) {
        try {
          prepared.push({ name: f.name, data: await this.resizeImage(f) });
        } catch (e) {
          this.notify("Could not read " + f.name);
        }
      }
      if (!prepared.length) return;
      if (mode === "log") {
        this.pendingFiles = this.pendingFiles.concat(prepared);
        return;
      }
      this.uploading = true;
      for (const p of prepared) {
        await this.uploadOne(this.selected.id, p);
      }
      this.uploading = false;
      this.loadAttachments();
    },
    uploadOne(faultId, p) {
      return this.faultApi()
        .post("/ispFault/" + faultId + "/attachments", { name: p.name, data: p.data })
        .catch(() => {
          this.notify("Could not upload " + p.name);
        });
    },
    loadAttachments() {
      if (!this.selected) return;
      const faultId = this.selected.id;
      this.faultApi()
        .get("/ispFault/" + faultId + "/attachments")
        .then(response => {
          this.attachments = response.data;
          this.attachments.forEach(a => this.loadThumb(faultId, a.id));
        })
        .catch(() => {});
    },
    loadThumb(faultId, attachmentId) {
      this.faultApi()
        .get("/ispFault/" + faultId + "/attachments/" + attachmentId)
        .then(response => {
          this.$set(this.thumbs, attachmentId, response.data);
        })
        .catch(() => {});
    },
    openAttachment(a) {
      const src = this.thumbs[a.id];
      if (!src) return;
      this.viewerSrc = src;
      this.viewer = true;
    },
    loadComments() {
      this.faultApi()
        .get("/ispFault/" + this.selected.id + "/comments")
        .then(response => {
          this.comments = response.data;
        })
        .catch(() => {
          this.notify("Could not load comments.");
        });
    },
    saveStatus() {
      this.savingStatus = true;
      this.faultApi()
        .put("/ispFault/" + this.selected.id, {
          status: this.selected.status,
          resolutionNote: this.selected.resolutionNote
        })
        .then(() => {
          this.savingStatus = false;
          this.notify("Fault updated.");
          this.loadFaults();
        })
        .catch(error => {
          this.savingStatus = false;
          this.notify(
            (error.response && error.response.data && error.response.data.error) ||
              "Could not update the fault."
          );
        });
    },
    addComment() {
      if (!this.newComment) return;
      this.savingComment = true;
      this.faultApi()
        .post("/ispFault/" + this.selected.id + "/comments", {
          comment: this.newComment
        })
        .then(() => {
          this.savingComment = false;
          this.newComment = "";
          this.loadComments();
        })
        .catch(() => {
          this.savingComment = false;
          this.notify("Could not add the comment.");
        });
    },
    // The server already permits an ISP to set Open - only Resolved is
    // restricted to the FNO - so this needs no server-side permission change.
    // The reason is posted as a comment first, so the thread records why.
    reopenFault() {
      if (!this.reopenReason) return;
      this.reopening = true;
      const reason = this.reopenReason;
      this.faultApi()
        .post("/ispFault/" + this.selected.id + "/comments", {
          comment: "Reopened by the ISP: " + reason
        })
        .then(() => this.faultApi().put("/ispFault/" + this.selected.id, { status: "Open" }))
        .then(() => {
          this.reopening = false;
          this.reopenReason = "";
          this.selected.status = "Open";
          this.notify("Fault reopened.");
          this.loadComments();
          this.loadFaults();
        })
        .catch(() => {
          this.reopening = false;
          this.notify("Could not reopen the fault.");
        });
    },
    // Month to date, the range asked for most often.
    thisMonth() {
      const now = new Date();
      const pad = n => (n < 10 ? "0" : "") + n;
      const ymd = d => d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
      this.dateFrom = ymd(new Date(now.getFullYear(), now.getMonth(), 1));
      this.dateTo = ymd(now);
    },

    // Built in the browser from the rows already loaded. A server round trip
    // would only create a way for the export to disagree with the screen.
    exportCsv() {
      const q = v => {
        if (v === null || v === undefined || v === "") return '""';
        return '"' + String(v).replace(/"/g, '""') + '"';
      };
      const list = this.filteredFaults || [];
      const c = this.slaCounts;
      const period =
        (this.dateFrom || "the beginning") + " to " + (this.dateTo || "today");

      const lines = [];
      lines.push(q("Purple Forest - fault report"));
      lines.push([q("Period"), q(period)].join(","));
      lines.push([q("Generated"), q(new Date().toLocaleString("en-ZA"))].join(","));
      lines.push("");
      lines.push([q("Logged"), q(list.length)].join(","));
      lines.push([q("Resolved"), q(list.filter(f => f.status === "Resolved").length)].join(","));
      lines.push([q("Still open"), q(c.open)].join(","));
      lines.push([q("SLA met"), q(c.met)].join(","));
      lines.push([q("SLA at risk"), q(c.atRisk)].join(","));
      lines.push([q("SLA breached"), q(c.breached)].join(","));
      lines.push("");

      lines.push([
        "Reference", "ISP", "Estate", "Unit", "Package", "Down Mbps", "Up Mbps",
        "Category", "Status", "Contractor", "Contractor status", "Summary", "Logged", "Logged by", "Resolved",
        "Acknowledge", "Acknowledge due", "Acknowledged at",
        "Restore", "Restore due", "Restored at"
      ].map(q).join(","));

      list.forEach(f => {
        lines.push([
          f.reference,
          f.ispName || (f.customerOrganisationId ? "" : "Own fault"),
          this.estateFor(f),
          f.unitReference,
          f.packageName || f.packageCode,
          f.downloadMbps,
          f.uploadMbps,
          f.category,
          f.status,
          f.contractorName,
          f.referralStatus,
          f.title,
          this.formatDate(f.createdAt),
          f.loggedByName,
          this.formatDate(f.resolvedAt),
          f.slaAckStatus,
          this.formatDate(f.slaAckDueAt),
          this.formatDate(f.slaAckAt),
          f.slaRestoreStatus,
          this.formatDate(f.slaRestoreDueAt),
          this.formatDate(f.slaRestoreAt)
        ].map(q).join(","));
      });

      // The byte order mark is what makes Excel read the file as UTF-8 instead
      // of mangling estate names.
      const blob = new Blob(["\ufeff" + lines.join("\r\n")], {
        type: "text/csv;charset=utf-8;"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        "fault-report-" + new Date().toISOString().slice(0, 10) + ".csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    // The queue column shows whichever of the two clocks is in the most
    // trouble, so one glance says whether a fault needs attention. A stopped
    // clock reads Met, a running one OK / At risk / Breached.
    slaLabel(item) {
      const rank = { Breached: 3, "At risk": 2, OK: 1, Met: 0 };
      const a = item && item.slaAckStatus;
      const r = item && item.slaRestoreStatus;
      if (!a && !r) return "";
      if (!a) return r;
      if (!r) return a;
      return rank[a] >= rank[r] ? a : r;
    },

    slaColour(status) {
      if (status === "Breached") return "red";
      if (status === "At risk") return "orange";
      return "green";
    },

    statusColour(status) {
      if (status === "Resolved") return "green";
      if (status === "In Progress") return "orange";
      return "red";
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleString("en-ZA");
    },
    notify(text) {
      this.snackText = text;
      this.snack = true;
    }
  }
};
</script>

<style scoped>
.sla-tile {
  border-radius: 14px;
}
</style>
